import React, { useState } from 'react';
import { useApp } from '../context/AppContext';

const COLORS = ['#c9a96e','#8b7355','#4a6741','#2d5a8e','#7b3f6e','#3d6b8e','#8e4a3d','#4a7b6b','#6b4a8e','#5a5a5a'];

function StatCard({ label, value, color, sub }) {
  return (
    <div style={{ background: '#fff', borderRadius: 3, padding: '1rem 1.2rem', border: '1px solid #e8e8e8', flex: 1, minWidth: 130 }}>
      <div style={{ fontSize: 11, color: '#999', fontWeight: 600, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || '#1c1c1c', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#bbb', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

function SectionHeader({ title }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1rem' }}>
      <h2 style={{ margin: 0, color: '#1c1c1c', fontSize: 16, fontWeight: 700, letterSpacing: '-0.2px' }}>{title}</h2>
      <div style={{ flex: 1, height: 1, background: '#ebebeb' }} />
    </div>
  );
}

// Responsive grid: side-by-side on desktop, stacked on mobile
const twoColGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: '1.5rem',
};

const labelStyle = { display: 'block', fontSize: 11, color: '#888', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' };
const btnPrimary = { padding: '9px 18px', borderRadius: 3, background: '#1c1c1c', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' };
const btnSecondary = { padding: '9px 18px', borderRadius: 3, background: '#fff', color: '#555', border: '1px solid #ddd', cursor: 'pointer', fontSize: 12, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' };
const btnDanger = { padding: '9px 18px', borderRadius: 3, background: '#fff', color: '#b94040', border: '1px solid #f0d0d0', cursor: 'pointer', fontSize: 12, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' };
const btnAccent = { padding: '9px 18px', borderRadius: 3, background: '#c9a96e', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' };

export default function AdminDashboard() {
  const { artists, jobs, cones, expenses, addJob, updateJob, addCone, addExpense,
    deleteJob, deleteCone, deleteExpense, togglePresent, checkIn, checkOut,
    addArtist, updateArtist, removeArtist, getSummary, logout, currentUser, tick } = useApp();

  const [tab, setTab] = useState('dashboard');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [jobForm, setJobForm] = useState({ artistId: '', amount: '', note: '', date: new Date().toISOString().split('T')[0] });
  const [editingJob, setEditingJob] = useState(null); // job being edited
  const [editJobAmount, setEditJobAmount] = useState('');
  const [editJobNote, setEditJobNote] = useState('');

  const [coneForm, setConeForm] = useState({ qty: '', price: '', note: '', date: new Date().toISOString().split('T')[0], organic: false, artistId: '' });
  const [expForm, setExpForm] = useState({ description: '', amount: '', date: new Date().toISOString().split('T')[0] });
  const [artistForm, setArtistForm] = useState({ name: '', commission: '', pin: '', color: COLORS[0] });
  const [editArtist, setEditArtist] = useState(null);
  const [msg, setMsg] = useState('');

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 2500); };

  const today = new Date().toISOString().split('T')[0];
  const summary = getSummary(dateFrom || undefined, dateTo || undefined);
  const todaySummary = getSummary(today, today);

  const getWorkingTime = (artist) => {
    if (!artist.active || !artist.checkInTime) return null;
    const mins = Math.round((Date.now() - new Date(artist.checkInTime)) / 60000);
    const h = Math.floor(mins / 60), m = mins % 60;
    return `${h}h ${m}m`;
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'artists', label: 'Artists' },
    { id: 'jobs', label: 'Jobs' },
    { id: 'cones', label: 'Cone Sales' },
    { id: 'expenses', label: 'Expenses' },
    { id: 'reports', label: 'Reports' },
  ];

  const inp = { padding: '9px 11px', borderRadius: 3, border: '1px solid #ddd', fontSize: 13, outline: 'none', background: '#fafafa', color: '#1c1c1c', width: '100%', boxSizing: 'border-box' };

  const submitJob = (e) => {
    e.preventDefault();
    if (!jobForm.artistId || !jobForm.amount) return;
    addJob({ artistId: jobForm.artistId, amount: parseFloat(jobForm.amount), note: jobForm.note, date: jobForm.date });
    setJobForm({ artistId: '', amount: '', note: '', date: new Date().toISOString().split('T')[0] });
    flash('Job entry saved.');
  };

  const saveEditJob = (id) => {
    if (!editJobAmount) return;
    updateJob(id, { amount: parseFloat(editJobAmount), note: editJobNote });
    setEditingJob(null);
    flash('Job updated.');
  };

  const submitCone = (e) => {
    e.preventDefault();
    addCone({ qty: parseInt(coneForm.qty), price: parseFloat(coneForm.price), note: coneForm.note, date: coneForm.date, organic: coneForm.organic, artistId: coneForm.organic ? coneForm.artistId : null });
    setConeForm({ qty: '', price: '', note: '', date: new Date().toISOString().split('T')[0], organic: false, artistId: '' });
    flash('Cone sale recorded.');
  };

  const submitExpense = (e) => {
    e.preventDefault();
    addExpense({ description: expForm.description, amount: parseFloat(expForm.amount), date: expForm.date });
    setExpForm({ description: '', amount: '', date: new Date().toISOString().split('T')[0] });
    flash('Expense recorded.');
  };

  const submitArtist = (e) => {
    e.preventDefault();
    if (editArtist) {
      updateArtist(editArtist.id, { name: artistForm.name, commission: parseFloat(artistForm.commission), pin: artistForm.pin, color: artistForm.color });
      setEditArtist(null);
      flash('Artist updated.');
    } else {
      addArtist({ name: artistForm.name, commission: parseFloat(artistForm.commission), pin: artistForm.pin, color: artistForm.color });
      flash('Artist added.');
    }
    setArtistForm({ name: '', commission: '', pin: '', color: COLORS[0] });
  };

  const startEdit = (a) => {
    setEditArtist(a);
    setArtistForm({ name: a.name, commission: a.commission, pin: a.pin, color: a.color });
    setTab('artists');
    window.scrollTo(0, 0);
  };

  const statusBadge = (active, activeColor, activeLabel, inactiveLabel) => ({
    fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 2,
    background: active ? activeColor + '18' : '#f5f5f5',
    color: active ? activeColor : '#bbb',
    border: `1px solid ${active ? activeColor + '44' : '#ebebeb'}`,
    letterSpacing: '0.03em'
  });

  return (
    <div style={{ minHeight: '100vh', background: '#f5f4f2', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>

      {/* Top nav */}
      <div style={{ background: '#1c1c1c', padding: '0 1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 52 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 20, height: 20, border: '2px solid #c9a96e', borderRadius: 2, flexShrink: 0 }} />
          <span style={{ color: '#fff', fontWeight: 700, fontSize: 15, fontFamily: 'Georgia, serif', letterSpacing: '-0.2px' }}>Mehendi by Marjana</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ color: '#888', fontSize: 12, display: 'none' }}>{currentUser.name}</span>
          <button onClick={logout} style={{ padding: '5px 12px', borderRadius: 3, background: 'transparent', color: '#888', border: '1px solid #444', cursor: 'pointer', fontSize: 12 }}>Sign out</button>
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e8e8e8', display: 'flex', overflowX: 'auto', padding: '0 0.5rem' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '13px 14px', background: 'none', border: 'none',
            borderBottom: tab === t.id ? '2px solid #1c1c1c' : '2px solid transparent',
            color: tab === t.id ? '#1c1c1c' : '#888',
            fontWeight: tab === t.id ? 700 : 400,
            cursor: 'pointer', whiteSpace: 'nowrap', fontSize: 13,
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {msg && <div style={{ background: '#f0f7f0', borderBottom: '1px solid #d0e8d0', padding: '9px 1rem', color: '#2e6b2e', fontSize: 13 }}>{msg}</div>}

      <div style={{ padding: '1rem', maxWidth: 1100, margin: '0 auto' }}>

        {/* ---- DASHBOARD ---- */}
        {tab === 'dashboard' && (
          <div>
            <SectionHeader title="Today's Summary" />
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: '2rem' }}>
              <StatCard label="Revenue" value={`৳${todaySummary.totalRevenue.toFixed(0)}`} />
              <StatCard label="Cone Revenue" value={`৳${todaySummary.coneRevenue.toFixed(0)}`} />
              <StatCard label="Organic Cones" value={`৳${todaySummary.organicConeRevenue.toFixed(0)}`} color="#4a6741" />
              <StatCard label="Artist Payouts" value={`৳${todaySummary.totalArtistPay.toFixed(0)}`} color="#b94040" />
              <StatCard label="Expenses" value={`৳${todaySummary.totalExpenses.toFixed(0)}`} color="#b94040" />
              <StatCard label="Net Profit" value={`৳${todaySummary.profit.toFixed(0)}`} color={todaySummary.profit >= 0 ? '#2e6b2e' : '#b94040'} sub={todaySummary.profit < 0 ? 'Loss' : 'Profit'} />
            </div>

            <SectionHeader title="Artist Status" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 10 }}>
              {artists.map(a => {
                const wt = getWorkingTime(a);
                const todayEarning = jobs.filter(j => j.artistId === a.id && j.date === today).reduce((s, j) => s + j.artistEarning, 0);
                return (
                  <div key={a.id} style={{ background: '#fff', borderRadius: 3, padding: '1rem', border: '1px solid #e8e8e8' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                      <div style={{ width: 36, height: 36, borderRadius: '50%', background: a.color || '#c9a96e', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 15, flexShrink: 0 }}>{a.name[0]}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#1c1c1c' }}>{a.name}</div>
                        <div style={{ fontSize: 11, color: '#aaa' }}>{a.commission}% commission</div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-end' }}>
                        <span style={statusBadge(a.present, '#2e6b2e')}>{a.present ? 'Present' : 'Absent'}</span>
                        <span style={statusBadge(a.active, '#c9a96e')}>{a.active ? 'Active' : 'Idle'}</span>
                      </div>
                    </div>
                    {wt && <div style={{ fontSize: 12, color: '#c9a96e', marginBottom: 8, fontWeight: 600 }}>Working: {wt}</div>}
                    <div style={{ fontSize: 12, color: '#555', marginBottom: 10 }}>Today's earning: <strong style={{ color: '#1c1c1c' }}>৳{todayEarning.toFixed(0)}</strong></div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => togglePresent(a.id)} style={{ ...btnSecondary, flex: 1, padding: '7px 6px', fontSize: 11 }}>{a.present ? 'Mark Absent' : 'Mark Present'}</button>
                      {a.present && (
                        <button onClick={() => a.active ? checkOut(a.id) : checkIn(a.id)} style={{ ...btnPrimary, flex: 1, padding: '7px 6px', fontSize: 11, background: a.active ? '#b94040' : '#1c1c1c' }}>
                          {a.active ? 'Check Out' : 'Check In'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ---- ARTISTS ---- */}
        {tab === 'artists' && (
          <div style={twoColGrid}>
            <div>
              <SectionHeader title={editArtist ? 'Edit Artist' : 'Add Artist'} />
              <div style={{ background: '#fff', borderRadius: 3, padding: '1.5rem', border: '1px solid #e8e8e8' }}>
                <form onSubmit={submitArtist} style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
                  <div><label style={labelStyle}>Full Name</label><input style={inp} placeholder="Artist name" value={artistForm.name} onChange={e => setArtistForm(p => ({ ...p, name: e.target.value }))} required /></div>
                  <div><label style={labelStyle}>Commission %</label><input style={inp} type="number" placeholder="e.g. 40" min="0" max="100" value={artistForm.commission} onChange={e => setArtistForm(p => ({ ...p, commission: e.target.value }))} required /></div>
                  <div><label style={labelStyle}>Login PIN</label><input style={inp} placeholder="4-digit PIN" value={artistForm.pin} onChange={e => setArtistForm(p => ({ ...p, pin: e.target.value }))} required /></div>
                  <div>
                    <label style={labelStyle}>Profile Color</label>
                    <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                      {COLORS.map(c => <div key={c} onClick={() => setArtistForm(p => ({ ...p, color: c }))} style={{ width: 26, height: 26, borderRadius: '50%', background: c, cursor: 'pointer', border: artistForm.color === c ? '2px solid #1c1c1c' : '2px solid transparent', outline: artistForm.color === c ? '1px solid #1c1c1c' : 'none', outlineOffset: 2 }} />)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    <button type="submit" style={btnPrimary}>{editArtist ? 'Update' : 'Add Artist'}</button>
                    {editArtist && <button type="button" onClick={() => { setEditArtist(null); setArtistForm({ name: '', commission: '', pin: '', color: COLORS[0] }); }} style={btnSecondary}>Cancel</button>}
                  </div>
                </form>
              </div>
            </div>

            <div>
              <SectionHeader title={`All Artists (${artists.length})`} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {artists.map(a => {
                  const totalEarned = jobs.filter(j => j.artistId === a.id).reduce((s, j) => s + j.artistEarning, 0);
                  const hours = Math.floor((a.totalWorkMinutes || 0) / 60);
                  return (
                    <div key={a.id} style={{ background: '#fff', borderRadius: 3, padding: '0.9rem 1rem', border: '1px solid #e8e8e8', display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 40, height: 40, borderRadius: '50%', background: a.color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 16, flexShrink: 0 }}>{a.name[0]}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, color: '#1c1c1c', fontSize: 14 }}>{a.name}</div>
                        <div style={{ fontSize: 12, color: '#aaa' }}>{a.commission}% &nbsp;·&nbsp; Earned: ৳{totalEarned.toFixed(0)} &nbsp;·&nbsp; {hours}h</div>
                      </div>
                      <button onClick={() => startEdit(a)} style={{ ...btnSecondary, padding: '6px 12px', fontSize: 11 }}>Edit</button>
                      <button onClick={() => { if (window.confirm(`Remove ${a.name}?`)) removeArtist(a.id); }} style={{ ...btnDanger, padding: '6px 12px', fontSize: 11 }}>Remove</button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ---- JOBS ---- */}
        {tab === 'jobs' && (
          <div style={twoColGrid}>
            <div>
              <SectionHeader title="New Job Entry" />
              <div style={{ background: '#fff', borderRadius: 3, padding: '1.5rem', border: '1px solid #e8e8e8' }}>
                <form onSubmit={submitJob} style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
                  <div>
                    <label style={labelStyle}>Artist</label>
                    <select style={inp} value={jobForm.artistId} onChange={e => setJobForm(p => ({ ...p, artistId: e.target.value }))} required>
                      <option value="">Select artist</option>
                      {artists.map(a => <option key={a.id} value={a.id}>{a.name} ({a.commission}%)</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Amount (৳)</label>
                    <input style={inp} type="number" placeholder="0.00" min="0" value={jobForm.amount} onChange={e => setJobForm(p => ({ ...p, amount: e.target.value }))} required />
                  </div>
                  {jobForm.artistId && jobForm.amount && (
                    <div style={{ background: '#f9f7f3', border: '1px solid #ebe5d8', borderRadius: 3, padding: '10px 12px', fontSize: 13 }}>
                      Artist payout: <strong>৳{((parseFloat(jobForm.amount) || 0) * (artists.find(a => a.id === jobForm.artistId)?.commission || 0) / 100).toFixed(0)}</strong>
                    </div>
                  )}
                  <div><label style={labelStyle}>Date</label><input style={inp} type="date" value={jobForm.date} onChange={e => setJobForm(p => ({ ...p, date: e.target.value }))} /></div>
                  <div><label style={labelStyle}>Note (optional)</label><input style={inp} placeholder="e.g. bridal, simple design" value={jobForm.note} onChange={e => setJobForm(p => ({ ...p, note: e.target.value }))} /></div>
                  <button type="submit" style={btnPrimary}>Save Job</button>
                </form>
              </div>
            </div>

            <div>
              <SectionHeader title={`Job Records (${jobs.length})`} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7, maxHeight: 600, overflowY: 'auto' }}>
                {[...jobs].reverse().map(j => {
                  const artist = artists.find(a => a.id === j.artistId);
                  const isEditing = editingJob === j.id;
                  const earning = j.artistEarning || 0;
                  const amount = j.amount || 0;
                  const commission = j.commission || 0;
                  return (
                    <div key={j.id} style={{ background: '#fff', borderRadius: 3, border: '1px solid #e8e8e8', overflow: 'hidden', flexShrink: 0 }}>
                      <div style={{ padding: '11px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                        {artist && <div style={{ width: 30, height: 30, borderRadius: '50%', background: artist.color || '#c9a96e', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>{(artist.name || '?')[0]}</div>}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: '#1c1c1c' }}>{artist?.name || 'Unknown'} — ৳{amount}</div>
                          <div style={{ fontSize: 11, color: '#aaa' }}>{j.date || 'No date'} · Payout: ৳{earning.toFixed ? earning.toFixed(0) : earning} ({commission}%){j.note ? ` · ${j.note}` : ''}</div>
                        </div>
                        <button onClick={() => { setEditingJob(isEditing ? null : j.id); setEditJobAmount(amount); setEditJobNote(j.note || ''); }} style={{ ...btnSecondary, padding: '5px 10px', fontSize: 11 }}>Edit</button>
                        <button onClick={() => { if (window.confirm('Delete this job?')) deleteJob(j.id); }} style={{ background: 'none', border: 'none', color: '#ddd', cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: '0 4px' }}>×</button>
                      </div>
                      {isEditing && (
                        <div style={{ padding: '12px 14px', background: '#f9f7f3', borderTop: '1px solid #ebe5d8', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                          <div style={{ flex: 1, minWidth: 120 }}>
                            <label style={{ ...labelStyle, marginBottom: 3 }}>New Amount (৳)</label>
                            <input style={{ ...inp, background: '#fff' }} type="number" min="0" value={editJobAmount} onChange={e => setEditJobAmount(e.target.value)} />
                          </div>
                          <div style={{ flex: 2, minWidth: 140 }}>
                            <label style={{ ...labelStyle, marginBottom: 3 }}>Note</label>
                            <input style={{ ...inp, background: '#fff' }} value={editJobNote} onChange={e => setEditJobNote(e.target.value)} placeholder="Optional note" />
                          </div>
                          {editJobAmount && (
                            <div style={{ fontSize: 12, color: '#888', alignSelf: 'center', whiteSpace: 'nowrap' }}>
                              Payout: ৳{((parseFloat(editJobAmount) || 0) * (artists.find(a => a.id === j.artistId)?.commission || 0) / 100).toFixed(0)}
                            </div>
                          )}
                          <button onClick={() => saveEditJob(j.id)} style={{ ...btnPrimary, padding: '9px 14px' }}>Save</button>
                          <button onClick={() => setEditingJob(null)} style={{ ...btnSecondary, padding: '9px 14px' }}>Cancel</button>
                        </div>
                      )}
                    </div>
                  );
                })}
                {jobs.length === 0 && <div style={{ color: '#ccc', textAlign: 'center', padding: '2.5rem', fontSize: 13 }}>No job records yet.</div>}
              </div>
            </div>
          </div>
        )}

        {/* ---- CONES ---- */}
        {tab === 'cones' && (
          <div style={twoColGrid}>
            <div>
              <SectionHeader title="New Cone Sale" />
              <div style={{ background: '#fff', borderRadius: 3, padding: '1.5rem', border: '1px solid #e8e8e8' }}>
                <form onSubmit={submitCone} style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
                  {/* Organic toggle */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: coneForm.organic ? '#f0f5ee' : '#fafafa', borderRadius: 3, border: `1px solid ${coneForm.organic ? '#c8ddc4' : '#eee'}`, cursor: 'pointer' }} onClick={() => setConeForm(p => ({ ...p, organic: !p.organic, artistId: '' }))}>
                    <div style={{ width: 18, height: 18, borderRadius: 3, border: `2px solid ${coneForm.organic ? '#4a6741' : '#ccc'}`, background: coneForm.organic ? '#4a6741' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {coneForm.organic && <div style={{ width: 8, height: 8, background: '#fff', borderRadius: 1 }} />}
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#1c1c1c' }}>Organic Mehendi Cone</div>
                      <div style={{ fontSize: 11, color: '#888' }}>Full price collected by studio. Assign to artist for tracking.</div>
                    </div>
                  </div>

                  {coneForm.organic && (
                    <div>
                      <label style={labelStyle}>Artist who used it</label>
                      <select style={inp} value={coneForm.artistId} onChange={e => setConeForm(p => ({ ...p, artistId: e.target.value }))} required={coneForm.organic}>
                        <option value="">Select artist</option>
                        {artists.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                      </select>
                    </div>
                  )}

                  <div><label style={labelStyle}>Quantity</label><input style={inp} type="number" placeholder="Number of cones" min="1" value={coneForm.qty} onChange={e => setConeForm(p => ({ ...p, qty: e.target.value }))} required /></div>
                  <div><label style={labelStyle}>Price per cone (৳)</label><input style={inp} type="number" placeholder="0.00" min="0" value={coneForm.price} onChange={e => setConeForm(p => ({ ...p, price: e.target.value }))} required /></div>
                  {coneForm.qty && coneForm.price && (
                    <div style={{ background: '#f9f7f3', border: '1px solid #ebe5d8', borderRadius: 3, padding: '10px 12px', fontSize: 13 }}>
                      Total: <strong>৳{(parseInt(coneForm.qty) * parseFloat(coneForm.price)).toFixed(0)}</strong>
                      {coneForm.organic && <span style={{ color: '#4a6741', marginLeft: 8, fontSize: 12 }}>— Goes to studio</span>}
                    </div>
                  )}
                  <div><label style={labelStyle}>Date</label><input style={inp} type="date" value={coneForm.date} onChange={e => setConeForm(p => ({ ...p, date: e.target.value }))} /></div>
                  <div><label style={labelStyle}>Note (optional)</label><input style={inp} placeholder="Optional note" value={coneForm.note} onChange={e => setConeForm(p => ({ ...p, note: e.target.value }))} /></div>
                  <button type="submit" style={btnPrimary}>Save Sale</button>
                </form>
              </div>
            </div>

            <div>
              <SectionHeader title={`Cone Sales — ${cones.reduce((s, c) => s + c.qty, 0)} total`} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7, maxHeight: 600, overflowY: 'auto' }}>
                {[...cones].reverse().map(c => {
                  const artist = c.artistId ? artists.find(a => a.id === c.artistId) : null;
                  return (
                    <div key={c.id} style={{ background: '#fff', borderRadius: 3, padding: '11px 14px', border: `1px solid ${c.organic ? '#c8ddc4' : '#e8e8e8'}`, display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                      <div style={{ width: 30, height: 30, borderRadius: 3, background: c.organic ? '#f0f5ee' : '#f5f0e8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <div style={{ width: 12, height: 12, borderRadius: '50%', background: c.organic ? '#4a6741' : '#c9a96e' }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: '#1c1c1c' }}>
                          {c.qty} &times; ৳{c.price} = ৳{(c.qty * c.price).toFixed(0)}
                          {c.organic && <span style={{ marginLeft: 8, fontSize: 11, color: '#4a6741', fontWeight: 600, background: '#f0f5ee', padding: '1px 6px', borderRadius: 2 }}>Organic</span>}
                        </div>
                        <div style={{ fontSize: 11, color: '#aaa' }}>{c.date}{artist && ` · Used by ${artist.name}`}{c.note && ` · ${c.note}`}</div>
                      </div>
                      <button onClick={() => { if (window.confirm('Delete?')) deleteCone(c.id); }} style={{ background: 'none', border: 'none', color: '#ddd', cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: '0 4px' }}>×</button>
                    </div>
                  );
                })}
                {cones.length === 0 && <div style={{ color: '#ccc', textAlign: 'center', padding: '2.5rem', fontSize: 13 }}>No cone sales recorded.</div>}
              </div>
            </div>
          </div>
        )}

        {/* ---- EXPENSES ---- */}
        {tab === 'expenses' && (
          <div style={twoColGrid}>
            <div>
              <SectionHeader title="New Expense" />
              <div style={{ background: '#fff', borderRadius: 3, padding: '1.5rem', border: '1px solid #e8e8e8' }}>
                <form onSubmit={submitExpense} style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
                  <div><label style={labelStyle}>Description</label><input style={inp} placeholder="What was this expense for?" value={expForm.description} onChange={e => setExpForm(p => ({ ...p, description: e.target.value }))} required /></div>
                  <div><label style={labelStyle}>Amount (৳)</label><input style={inp} type="number" placeholder="0.00" min="0" value={expForm.amount} onChange={e => setExpForm(p => ({ ...p, amount: e.target.value }))} required /></div>
                  <div><label style={labelStyle}>Date</label><input style={inp} type="date" value={expForm.date} onChange={e => setExpForm(p => ({ ...p, date: e.target.value }))} /></div>
                  <button type="submit" style={btnPrimary}>Save Expense</button>
                </form>
              </div>
            </div>

            <div>
              <SectionHeader title="Expense Records" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7, maxHeight: 600, overflowY: 'auto' }}>
                {[...expenses].reverse().map(e => (
                  <div key={e.id} style={{ background: '#fff', borderRadius: 3, padding: '11px 14px', border: '1px solid #e8e8e8', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#1c1c1c' }}>{e.description} &mdash; <span style={{ color: '#b94040' }}>৳{e.amount.toFixed(0)}</span></div>
                      <div style={{ fontSize: 11, color: '#aaa' }}>{e.date}</div>
                    </div>
                    <button onClick={() => { if (window.confirm('Delete?')) deleteExpense(e.id); }} style={{ background: 'none', border: 'none', color: '#ddd', cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: '0 4px' }}>×</button>
                  </div>
                ))}
                {expenses.length === 0 && <div style={{ color: '#ccc', textAlign: 'center', padding: '2.5rem', fontSize: 13 }}>No expenses recorded.</div>}
              </div>
            </div>
          </div>
        )}

        {/* ---- REPORTS ---- */}
        {tab === 'reports' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1.5rem', flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, color: '#1c1c1c', fontSize: 16, fontWeight: 700 }}>Financial Reports</h2>
              <div style={{ flex: 1, height: 1, background: '#ebebeb' }} />
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <label style={{ fontSize: 12, color: '#888' }}>From</label>
                <input type="date" style={{ ...inp, width: 'auto' }} value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                <label style={{ fontSize: 12, color: '#888' }}>To</label>
                <input type="date" style={{ ...inp, width: 'auto' }} value={dateTo} onChange={e => setDateTo(e.target.value)} />
                <button onClick={() => { setDateFrom(''); setDateTo(''); }} style={btnSecondary}>Clear</button>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: '2rem' }}>
              <StatCard label="Job Revenue" value={`৳${summary.totalRevenue.toFixed(0)}`} />
              <StatCard label="Cone Revenue" value={`৳${summary.coneRevenue.toFixed(0)}`} />
              <StatCard label="Organic Cones" value={`৳${summary.organicConeRevenue.toFixed(0)}`} color="#4a6741" />
              <StatCard label="Gross Income" value={`৳${summary.grossIncome.toFixed(0)}`} />
              <StatCard label="Artist Payouts" value={`৳${summary.totalArtistPay.toFixed(0)}`} color="#b94040" />
              <StatCard label="Expenses" value={`৳${summary.totalExpenses.toFixed(0)}`} color="#b94040" />
              <StatCard label="Net Profit" value={`৳${summary.profit.toFixed(0)}`} color={summary.profit >= 0 ? '#2e6b2e' : '#b94040'} sub={summary.profit < 0 ? 'Net Loss' : 'Net Profit'} />
            </div>

            <SectionHeader title="Artist Breakdown" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', gap: 10 }}>
              {artists.map(a => {
                const aJobs = jobs.filter(j => j.artistId === a.id && (!dateFrom || j.date >= dateFrom) && (!dateTo || j.date <= dateTo));
                const earned = aJobs.reduce((s, j) => s + j.artistEarning, 0);
                const rev = aJobs.reduce((s, j) => s + j.amount, 0);
                const totalMins = a.totalWorkMinutes || 0;
                const hours = Math.floor(totalMins / 60);
                const mins = totalMins % 60;
                const organicUsed = cones.filter(c => c.organic && c.artistId === a.id && (!dateFrom || c.date >= dateFrom) && (!dateTo || c.date <= dateTo));
                const organicQty = organicUsed.reduce((s, c) => s + c.qty, 0);
                const organicRevenue = organicUsed.reduce((s, c) => s + c.qty * c.price, 0);
                return (
                  <div key={a.id} style={{ background: '#fff', borderRadius: 3, padding: '1rem', border: '1px solid #e8e8e8' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, paddingBottom: 10, borderBottom: '1px solid #f0f0f0' }}>
                      <div style={{ width: 36, height: 36, borderRadius: '50%', background: a.color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 15, flexShrink: 0 }}>{a.name[0]}</div>
                      <div><div style={{ fontWeight: 700, color: '#1c1c1c', fontSize: 14 }}>{a.name}</div><div style={{ fontSize: 11, color: '#aaa' }}>{a.commission}% commission</div></div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7 }}>
                      {[
                        { label: 'Jobs', value: aJobs.length },
                        { label: 'Revenue', value: `৳${rev.toFixed(0)}` },
                        { label: 'Payout', value: `৳${earned.toFixed(0)}`, highlight: true },
                        { label: 'Hours', value: `${hours}h ${mins}m` },
                      ].map(s => (
                        <div key={s.label} style={{ background: s.highlight ? '#f9f7f3' : '#fafafa', borderRadius: 3, padding: '8px 10px', border: s.highlight ? '1px solid #ebe5d8' : '1px solid #f0f0f0' }}>
                          <div style={{ fontSize: 10, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>{s.label}</div>
                          <div style={{ fontWeight: 700, color: '#1c1c1c', fontSize: 15 }}>{s.value}</div>
                        </div>
                      ))}
                    </div>
                    {organicQty > 0 && (
                      <div style={{ marginTop: 8, padding: '8px 10px', background: '#f0f5ee', borderRadius: 3, border: '1px solid #c8ddc4', fontSize: 12 }}>
                        <span style={{ color: '#4a6741', fontWeight: 600 }}>Organic cones used: {organicQty}</span>
                        <span style={{ color: '#888', marginLeft: 8 }}>৳{organicRevenue.toFixed(0)} to studio</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
